import { DEFAULTS } from '../pages/Register/RegistrationSuccess/RegistrationSuccess';

export const getSuccessAlertTitle = ({ alertMessage, registrationData }) => {
  if (!alertMessage) return DEFAULTS.ALERT;

  const email = registrationData?.['Email Address'] || '';

  if (!!email) return `${alertMessage} for ${email}.`;

  return `${alertMessage}.`;
};

export const isHtml = (input) => {
  if (!input || typeof input !== 'string') return false;

  // quick check for any angle brackets
  if (input.indexOf('<') === -1 || input.indexOf('>') === -1) return false;

  const doc = new DOMParser().parseFromString(input, 'text/html');
  for (let i = 0; i < doc.body.childNodes.length; i++) {
    if (doc.body.childNodes[i].nodeType === 1) {
      return true;
    }
  }

  if (doc.body.querySelector('*')) return true;

  // Edge: some HTML like a single <br> might live as an element node,
  // but entity-only strings like "&nbsp;" become text, not elements.
  return false;
};

/**
 * small whitelist of allowed tags for HTML sanitization, including safe <a> links.
 */
export const ALLOWED_TAGS = [
  'a',
  'b',
  'i',
  'em',
  'strong',
  'u',
  'br',
  'p',
  'span',
  'ul',
  'ol',
  'li',
];
export const allowedAttrs = {
  '*': [], // none by default
  a: ['href', 'title', 'target', 'rel'],
};
export const allowedSchemes = ['http:', 'https:', 'mailto:', 'tel:'];
export const allowRelative = true;

/**
 * Sanitize HTML with a small whitelist of allowed tags, including safe <a> links.
 * - Removes disallowed tags but keeps their children (so text/content is preserved)
 * - Strips all event attrs (on*)
 * - For <a>, only allows safe href schemes: http, https, mailto, tel, and relative URLs
 * - If target="_blank" is present, ensures rel includes "noopener noreferrer"
 */
export function sanitizeHtmlWithWhitelist(html) {
  if (!html || typeof html !== 'string') return '';

  const template = document.createElement('template');
  template.innerHTML = html;

  // remove <script> and <style> entirely up front
  template.content.querySelectorAll('script, style').forEach((n) => n.remove());

  const isAllowedTag = (tag) => ALLOWED_TAGS.includes(tag.toLowerCase());

  const processNode = (node) => {
    // Element node
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();

      // Strip disallowed elements but keep their children (content preserved)
      if (!isAllowedTag(tag)) {
        node.replaceWith(...node.childNodes);
        return; // replaced; nothing else to do
      }

      // Remove event handler attrs (on*)
      for (const attr of [...node.attributes]) {
        if (/^on/i.test(attr.name)) node.removeAttribute(attr.name);
      }

      // Keep only whitelisted attributes for this tag
      const allowed = new Set([
        ...(allowedAttrs['*'] || []),
        ...(allowedAttrs?.[tag] || []),
      ]);

      for (const attr of [...node.attributes]) {
        if (!allowed.has(attr.name.toLowerCase())) {
          node.removeAttribute(attr.name);
          continue;
        }
        // Extra checks for <a>
        if (tag === 'a') {
          if (attr.name.toLowerCase() === 'href') {
            const safeHref = sanitizeHref(node.getAttribute('href'), {
              allowedSchemes,
              allowRelative,
            });
            if (safeHref == null) {
              node.removeAttribute('href'); // unsafe -> drop href
            } else {
              node.setAttribute('href', safeHref);
            }
          }
          if (attr.name.toLowerCase() === 'target') {
            const t = node.getAttribute('target');
            if (t && t.toLowerCase() === '_blank') {
              // ensure rel has noopener noreferrer
              const rel = (node.getAttribute('rel') || '')
                .toLowerCase()
                .split(/\s+/);
              const need = ['noopener', 'noreferrer'];
              need.forEach((v) => {
                if (!rel.includes(v)) rel.push(v);
              });
              node.setAttribute('rel', rel.filter(Boolean).join(' '));
            } else if (!t) {
              // no target: fine; nothing to add
            } else {
              // any other target is allowed but no special action
            }
          }
          if (attr.name.toLowerCase() === 'rel') {
            // strip potentially dangerous rel values like "opener"
            const safeRel = (node.getAttribute('rel') || '')
              .split(/\s+/)
              .filter(Boolean)
              .filter((v) => v.toLowerCase() !== 'opener')
              .join(' ');
            if (safeRel) node.setAttribute('rel', safeRel);
            else node.removeAttribute('rel');
          }
        }
      }
    }

    // Recurse into children (use a static array because we might mutate)
    for (const child of [...node.childNodes]) processNode(child);
  };

  processNode(template.content);

  return template.innerHTML;
}

/** Sanitize href values:
 * - Allow http/https/mailto/tel
 * - Optionally allow relative URLs (no scheme)
 * - Block javascript:, data:, vbscript:, file:, etc.
 */
function sanitizeHref(raw, { allowedSchemes, allowRelative }) {
  if (!raw) return null;

  // Trim & decode harmlessly
  const value = String(raw).trim();

  // Empty or just a hash can be allowed if you want:
  if (value === '' || value === '#') return value;

  // Use <a> to parse URL safely without external libs
  const a = document.createElement('a');
  a.href = value;

  // If value lacked a scheme (e.g., "/path" or "page"), browsers resolve it to absolute with current origin
  const hasExplicitScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);

  if (!hasExplicitScheme) {
    // relative or protocol-relative URL
    return allowRelative ? value : null;
  }

  // Check allowed schemes
  const scheme = (a.protocol || '').toLowerCase(); // includes trailing colon
  if (!allowedSchemes.includes(scheme)) return null;

  return value;
}
